import { Grid } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import useAnalyticsStore, {
  ISessionsFilter,
} from 'components/sections/analytics/store/use-analytics-store';
import { HittersContext } from 'contexts/hitters.context';
import { lightFormat } from 'date-fns';
import parseISO from 'date-fns/parseISO';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { LOCAL_DATE_FORMAT } from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ISHSummary } from 'lib_ts/interfaces/session-hits/i-summary';
import { useCallback, useContext, useEffect, useMemo } from 'react';

const getUniqueStartDates = (sessions: ISHSummary[]) =>
  ArrayHelper.unique(
    sessions.map((m) => lightFormat(parseISO(m.startDate), LOCAL_DATE_FORMAT))
  ).sort((a, b) => -StringHelper.safeSort(a, b));

export const SessionHitsSummariesToolbar = (props: {
  hideHitter?: boolean;
}) => {
  const { hitterOptions } = useContext(HittersContext);

  const sessions = useAnalyticsStore((s) => s.sessions);
  const sessionsFilter = useAnalyticsStore((s) => s.sessionsFilter);

  const startDates = useMemo(() => getUniqueStartDates(sessions), [sessions]);

  // filtered to what is actually found in sessions
  const sessionHitterOptions = useMemo(() => {
    if (props.hideHitter) {
      // nothing needed
      return [];
    }

    const hittersDict = ArrayHelper.firstByFn(
      sessions.filter((s) => s.hitterID),
      (s) => s.hitterID ?? ''
    );

    return hitterOptions.filter((o) => hittersDict[o.value] !== undefined);
  }, [props.hideHitter, sessions, hitterOptions]);

  const startDateOptions = useMemo(
    () =>
      startDates.map((startDate) => ({
        label: startDate,
        value: startDate,
      })),
    [startDates]
  );

  const mergeSessionsFilter = useCallback(
    (filter: Partial<ISessionsFilter>) =>
      useAnalyticsStore.setState((prev) => ({
        sessionsFilter: {
          ...prev.sessionsFilter,
          ...filter,
        },
      })),
    []
  );

  // if a hitter or start date filter is set to a value that is no longer valid (e.g. that session was deleted), unset that filter value
  useEffect(() => {
    const nextFilter: Partial<ISessionsFilter> = {};

    const validHitterFilter = sessionsFilter.hitterIDs.filter((id) =>
      sessionHitterOptions.find((o) => o.value === id)
    );

    if (validHitterFilter.length !== sessionsFilter.hitterIDs.length) {
      nextFilter.hitterIDs = validHitterFilter;
    }

    const validStartDateFilter = sessionsFilter.startDates.filter((date) =>
      startDates.includes(date)
    );

    if (validStartDateFilter.length !== sessionsFilter.startDates.length) {
      nextFilter.startDates = validStartDateFilter;
    }

    if (Object.keys(nextFilter).length === 0) {
      return;
    }

    mergeSessionsFilter(nextFilter);
  }, [sessionHitterOptions, startDates, sessionsFilter, mergeSessionsFilter]);

  return (
    <ErrorBoundary identifier="SessionHitsSummariesToolbar">
      <Grid columns={props.hideHitter ? '2' : '3'} gap={RADIX.FLEX.GAP.SM}>
        {!props.hideHitter && (
          <CommonSearchInput
            id="filter-name"
            placeholder="common.hitter-name"
            options={sessionHitterOptions}
            values={sessionsFilter.hitterIDs}
            onChange={(v) => mergeSessionsFilter({ hitterIDs: v })}
            multiple
            optional
            strict
          />
        )}
        <CommonSearchInput
          id="filter-session-start"
          placeholder="common.start-date"
          options={startDateOptions}
          values={sessionsFilter.startDates}
          onChange={(v) => mergeSessionsFilter({ startDates: v })}
          multiple
          optional
          skipSort
        />
        <CommonSelectInput
          id="filter-session-type"
          placeholder="common.session-type"
          options={[
            {
              label: 'In-Game',
              value: 'true',
            },
            {
              label: 'Practice',
              value: 'false',
            },
          ]}
          value={sessionsFilter.isInGame?.toString()}
          onOptionalBooleanChange={(v) => mergeSessionsFilter({ isInGame: v })}
          optional
        />
      </Grid>
    </ErrorBoundary>
  );
};
